import { defineStore } from 'pinia';
import { ref } from 'vue';
import _ from 'lodash';
import Utils from 'ui-component-library/helpers/Utils';
import { UserDetails } from '@/types';
import RequestsUtils from '@/helpers/RequestsUtils';
import { AxiosError, AxiosResponse } from 'axios';
// @ts-expect-error miss type declaration
import { idleTabs } from 'idle-tabs';
import { ENV } from '@/constants';
import { useContextStore } from '@/store/ContextStore';

const PLANET_URL = ENV.VITE_PLANET_URL;

const accessLevelWaapEventsViewer = 'waap-events-viewer';
const accessLevelWaapConfigViewer = 'waap-config-viewer';
const accessLevelWaapConfigEditor = 'waap-config-editor';
const accessLevelWaapManager = 'waap-manager';
const accessLevelWaapSystemAdmin = 'waap-system-admin';

export const useUserStore = defineStore('user', () => {
  const contextStore = useContextStore();
  // Data
  const _userIdentifier = ref(null);
  const _userFromShell = ref(null);
  const _rawTokenFromShell = ref<string | null>(null); // Store raw JWT token from shell
  const _cookieName = ref('X-Token'); // Default: X-Token
  const _idleTracker = ref(null);
  const isIdle = ref(false);
  const _idleTrackStatusIntervalId = ref(null);
  const _idleUpdateRemainingTimeIntervalId = ref(null);
  const _idleModalEnabled = ref(false); // Default: false
  const idleReminderTime = ref(5); // Default: 5 minutes
  const idleMaxTime = ref(10); // Default: 10 minutes
  const sessionRemainingTime = ref(null);
  const _changeSubscribes = ref([]);
  const accessLevels = ref({
    waapEventsViewer: [
      accessLevelWaapEventsViewer,
      accessLevelWaapConfigViewer,
      accessLevelWaapConfigEditor,
      accessLevelWaapManager,
      accessLevelWaapSystemAdmin,
    ],
    waapConfigViewer: [
      accessLevelWaapConfigViewer,
      accessLevelWaapConfigEditor,
      accessLevelWaapManager,
      accessLevelWaapSystemAdmin,
    ],
    waapConfigEditor: [
      accessLevelWaapConfigEditor,
      accessLevelWaapManager,
      accessLevelWaapSystemAdmin,
    ],
    waapManager: [accessLevelWaapManager, accessLevelWaapSystemAdmin],
    waapSystemAdmin: [accessLevelWaapSystemAdmin],
  });

  // Methods
  function loadConfiguration() {
    return RequestsUtils.sendRequest({
      methodName: 'GET',
      url: 'db/system/user',
    })
      .then((response: AxiosResponse) => {
        const newValue = response?.data;
        if (newValue) {
          _cookieName.value = !_.isNil(newValue.cookieName)
            ? newValue.cookieName
            : _cookieName.value;
          _idleModalEnabled.value = !_.isNil(newValue.idleModalEnabled)
            ? newValue.idleModalEnabled
            : _idleModalEnabled.value;
          idleReminderTime.value = !_.isNil(newValue.idleReminderTime)
            ? newValue.idleReminderTime
            : idleReminderTime.value;
          idleMaxTime.value = !_.isNil(newValue.idleMaxTime)
            ? newValue.idleMaxTime
            : idleMaxTime.value;
        }
      })
      .catch((err: AxiosError) => {
        console.log('Error while attempting to load user configuration');
        console.log(err);
      });
  }

  function getUserCookieValue() {
    return Utils.parseJwt(getRawToken());
  }

  function getRawToken(): string | null {
    if (contextStore.isShell) {
      return _rawTokenFromShell.value;
    } else {
      const decodedCookie: string = decodeURIComponent(document.cookie);
      const decodedCookieSplit: string[] = decodedCookie?.split(';') || [];
      let userCookie: string;
      _.some(decodedCookieSplit, (cookie) => {
        while (cookie.startsWith(' ')) {
          cookie = cookie.substring(1);
        }
        if (cookie.startsWith(`${_cookieName.value}=`)) {
          userCookie = cookie.substring(_cookieName.value.length, cookie.length);
          return true;
        }
      });
      return userCookie?.split('=')?.[1];
    }
  }

  function deleteUserCookie() {
    document.cookie = `${_cookieName.value}=; Path=/; Max-Age=-99999999;`;
  }

  function getUserPlanet() {
    if (contextStore.isShell) {
      return PLANET_URL;
    } else {
      return '';
    }
  }

  function checkAccessLevel(accessLevels: string[]) {
    const userCookieValue = getUserCookieValue();
    const userHasAccessLevel = userCookieValue.groups?.some((group: string) => {
      return accessLevels.includes(group);
    });
    return !!userHasAccessLevel || !!ENV.DEV;
  }

  function initializeIdleTracker() {
    const config = {
      idleTime: idleReminderTime.value,
      debug: false,
      events: ['mousemove', 'keydown', 'mousedown', 'touchstart'],
      onIdle: () => {
        const previousIsIdle = localStorage.getItem('isIdle') === 'true';
        if (!previousIsIdle && !isIdle.value) {
          const now = new Date();
          const secondsSinceEpoch = Math.round(now.getTime() / 1000);
          const previousLastSeen = Number(localStorage.getItem('lastSeen')) || 0;
          if (secondsSinceEpoch > previousLastSeen) {
            window.localStorage.setItem('lastSeen', String(secondsSinceEpoch));
          }
        }
        isIdle.value = true;
        window.localStorage.setItem('isIdle', String(isIdle.value));
      },
      keepTracking: true,
      keepAlive: true,
      startAtIdle: false,
      recurIdleCall: false,
      storePrefix: 'waap',
    };
    _idleTracker.value = new idleTabs.IdleClass(config, window, document);
  }

  function idleUpdateRemainingTime() {
    const idleRemainingTime = isIdle.value ? getIdleRemainingTime() : Infinity;
    const cookieRemainingTime = getCookieAboutToExpire() ? getCookieRemainingTime() : Infinity;
    sessionRemainingTime.value = Math.min(idleRemainingTime, cookieRemainingTime);

    const previousIsIdle = localStorage.getItem('isIdle') === 'true';
    if (!previousIsIdle || !isIdle.value) {
      clearInterval(_idleUpdateRemainingTimeIntervalId.value);
      _idleUpdateRemainingTimeIntervalId.value = null;
      isIdle.value = false;
      window.localStorage.setItem('isIdle', String(isIdle.value));
    }

    if (sessionRemainingTime.value <= 0) {
      deleteUserCookie();
    }
  }

  function idleTrackStatus() {
    const cookieAboutToExpire = getCookieAboutToExpire();
    if (!isIdle.value && !cookieAboutToExpire) {
      sessionRemainingTime.value = null;
      clearInterval(_idleUpdateRemainingTimeIntervalId.value);
      _idleUpdateRemainingTimeIntervalId.value = null;
    } else if (
      !_idleUpdateRemainingTimeIntervalId.value &&
      (sessionRemainingTime.value > 0 || _.isNull(sessionRemainingTime.value))
    ) {
      _idleUpdateRemainingTimeIntervalId.value = setInterval(() => {
        idleUpdateRemainingTime();
      }, 1000);
      idleUpdateRemainingTime();
    }
  }

  async function idleTrackerStart() {
    await loadConfiguration();
    if (!_idleModalEnabled.value) {
      _idleTracker.value?.stop();
      return;
    }
    const now = new Date();
    window.localStorage.setItem('idle.waap', String(now.getTime()));
    initializeIdleTracker();
    _idleTracker.value.start();
    _idleTrackStatusIntervalId.value = setInterval(() => {
      idleTrackStatus();
    }, 1000);
    idleTrackStatus();
  }

  function idleTrackerStop() {
    _idleTracker.value?.stop();
    clearInterval(_idleTrackStatusIntervalId.value);
    _idleTrackStatusIntervalId.value = null;
  }

  function idleTrackerReset() {
    _idleTracker.value?.reset();
    sessionRemainingTime.value = null;
    clearInterval(_idleUpdateRemainingTimeIntervalId.value);
    _idleUpdateRemainingTimeIntervalId.value = null;
    isIdle.value = false;
    window.localStorage.setItem('isIdle', String(isIdle.value));
  }

  function getIdleRemainingTime() {
    // Cannot be a computed value as now date is not reactive
    const now = new Date();
    const secondsSinceEpoch = Math.round(now.getTime() / 1000);
    const lastSeen = Number(localStorage.getItem('lastSeen'));
    return idleMaxTime.value * 60 - idleReminderTime.value * 60 + lastSeen - secondsSinceEpoch;
  }

  function getCookieExpirationTime() {
    // Cannot be a computed value as cookies are not reactive
    const userCookieValue = getUserCookieValue();
    return Number(userCookieValue.exp);
  }

  function getCookieRemainingTime() {
    // Cannot be a computed value as cookies are not reactive
    const cookieExpirationTime = getCookieExpirationTime();
    const now = new Date();
    const secondsSinceEpoch = Math.round(now.getTime() / 1000);
    return cookieExpirationTime - secondsSinceEpoch;
  }

  function getCookieAboutToExpire() {
    // Cannot be a computed value as cookies are not reactive
    const cookieRemainingTime = getCookieRemainingTime();
    return cookieRemainingTime <= 10 * 60; // less than 10 minutes remain on cookie expiration
  }

  function getCookieAccountId(): string {
    // Cannot be a computed value as cookies are not reactive
    const userCookieValue = getUserCookieValue();
    return userCookieValue.sub;
  }

  function getCookieEmail(): string {
    // Cannot be a computed value as cookies are not reactive
    const userCookieValue = getUserCookieValue();
    return userCookieValue.email;
  }

  function getCookieName(): string {
    // Cannot be a computed value as cookies are not reactive
    const userCookieValue = getUserCookieValue();
    return userCookieValue.name;
  }

  function getCookieProfileProvider(): string {
    // Cannot be a computed value as cookies are not reactive
    const userCookieValue = getUserCookieValue();
    return userCookieValue.profile?.provider;
  }

  function accountDataChanged() {
    _changeSubscribes.value.forEach((callback) => {
      callback();
    });
  }

  function initialize(userData: object) {
    _userFromShell.value = userData;
  }

  function subscribeToChanges(callback: () => void) {
    const exists = _changeSubscribes.value.some((item) => {
      return _.isEqual(item, callback);
    });
    if (!exists) {
      _changeSubscribes.value.push(callback);
    }
  }

  function updateUserIdentifier(value: UserDetails) {
    _userIdentifier.value = {
      displayName: value['contact_name'],
      email: value.email,
    };
    // Fullstory identifier
    // @ts-expect-error FullStory global element not recognized by TS
    FS.identify(value.id, _userIdentifier.value);
  }

  async function logout() {
    Utils.clearSessionData();
    await RequestsUtils.sendAbsoluteRequest({
      methodName: 'POST',
      url: `/logout`,
    });
  }

  /**
   * Set user data and raw token from shell application
   * This is used when running as a micro-frontend within the shell
   * @param userData - Parsed JWT payload from shell
   * @param rawToken - Raw JWT token from shell for API authentication
   */
  function setTokenFromShell(rawToken: string): void {
    _rawTokenFromShell.value = rawToken || '';
  }

  return {
    accessLevels,
    checkAccessLevel,
    sessionRemainingTime,
    isIdle,
    idleReminderTime,
    idleMaxTime,
    idleTrackerStart,
    idleTrackerStop,
    idleTrackerReset,
    getCookieAccountId,
    getCookieEmail,
    getCookieName,
    getCookieProfileProvider,
    accountDataChanged,
    subscribeToChanges,
    updateUserIdentifier,
    logout,
    setTokenFromShell,
    getUserPlanet,
    getRawToken,
    getUserCookieValue,
  };
});
